/**
 * E06-F03-T13-S03 — DOM-free rules for "Website not found" + the manual website
 * input (Review) and the AI-check preflight UX (AI check screen).
 *
 * Owner rule: every relevant company HAS a website — we just did not find it. The UI
 * therefore says `WEBSITE_NOT_FOUND_LABEL` ("Website not found"), never "no website".
 * The AI check needs the website (scrape-first), so a row without one is not started:
 * the per-row buttons are disabled here, and the server refuses it anyway (422
 * `NO_COMPANY_WEBSITE` single / `skipped[].reason='no-website'` batch).
 */
import {
  AI_RUN_NO_WEBSITE_MESSAGE,
  AI_RUN_SKIP_NO_WEBSITE,
  type AiRunBatchResponse,
  type WebsiteStatus,
} from '@eer/shared';

/** Tooltip on a disabled AI-check button of a row without a website. */
export const ENTER_WEBSITE_FIRST = 'Enter the website first' as const;

/** Mirrors `setWebsiteBodySchema` (1–255 chars after trim). */
export const WEBSITE_MAX_LENGTH = 255;

interface WebsiteBearingRow {
  readonly domain?: string | null;
  readonly rawDomain?: string | null;
  readonly websiteStatus?: WebsiteStatus;
}

/** The row's website (server-resolved domain → rawDomain), or null. */
export function rowWebsite(row: WebsiteBearingRow): string | null {
  const value = (row.domain ?? row.rawDomain ?? '').trim();
  return value.length > 0 ? value : null;
}

/** True when the row shows "Website not found" (explicit status, or no domain at all). */
export function isWebsiteNotFound(row: WebsiteBearingRow): boolean {
  return row.websiteStatus === 'not_found' || rowWebsite(row) === null;
}

export type WebsiteInputResult =
  | { readonly ok: true; readonly value: string }
  | { readonly ok: false; readonly message: string };

/**
 * Client-side check before the PATCH: non-empty, ≤255, looks like `acme.com` or a
 * full URL. The server stays the authority (400 → shown inline as well).
 */
export function validateWebsiteInput(raw: string): WebsiteInputResult {
  const value = raw.trim();
  if (value.length === 0) {
    return { ok: false, message: 'Enter the company website, e.g. acme.com.' };
  }
  if (value.length > WEBSITE_MAX_LENGTH) {
    return {
      ok: false,
      message: `The website is too long (max ${WEBSITE_MAX_LENGTH} characters).`,
    };
  }
  const host = value.replace(/^https?:\/\//i, '').split(/[/?#]/)[0] ?? '';
  if (/\s/.test(value) || !host.includes('.') || host.startsWith('.') || host.endsWith('.')) {
    return { ok: false, message: 'Enter a website like acme.com or https://acme.com.' };
  }
  return { ok: true, value };
}

/** Inline text for a failed save (400 invalid · 409 AI check running / site conflict · 404). */
export function websiteSaveErrorMessage(
  detail: { readonly code?: string; readonly message?: string } | null | undefined,
): string {
  if (detail?.code === 'VALIDATION_ERROR') {
    return 'That does not look like a website — use acme.com or https://acme.com.';
  }
  const message = detail?.message?.trim();
  return message && message !== 'Unexpected error'
    ? message
    : 'Could not save the website — try again.';
}

/** Toast text for a refused single-row AI check (422 NO_COMPANY_WEBSITE) or any other failure. */
export function aiRunErrorMessage(
  err: { readonly message?: string } | null | undefined,
  fallback: string,
): string {
  return err?.message === AI_RUN_NO_WEBSITE_MESSAGE ? AI_RUN_NO_WEBSITE_MESSAGE : fallback;
}

/** How many batch rows the server skipped because the website is unknown. */
export function countNoWebsiteSkips(skipped: AiRunBatchResponse['skipped']): number {
  return skipped.filter((s) => s.reason === AI_RUN_SKIP_NO_WEBSITE).length;
}

/** Follow-up toast after a batch that skipped rows without a website. */
export function noWebsiteSkipMessage(count: number): string {
  return `${count} ${count === 1 ? 'company' : 'companies'} skipped — website not found; enter the site to check them.`;
}

/** Success toast for a batch start; skips other than "no website" are "already running". */
export function batchStartedMessage(result: AiRunBatchResponse): string {
  const started = result.enqueued.length;
  const running = result.skipped.length - countNoWebsiteSkips(result.skipped);
  return `AI check started for ${started} ${started === 1 ? 'company' : 'companies'}${
    running > 0 ? ` · ${running} skipped (already running)` : ''
  }.`;
}
